// src/components/sections/AboutDetails.tsx
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Users,
  Award,
  Code,
  Shield,
  Zap,
  Target,
  Heart,
  Lightbulb,
  Calendar,
  ExternalLink,
  Mail,
  Linkedin,
  Phone,
  Building2,
  Globe,
  X,
  CheckCircle2,
} from "lucide-react";
import Sidebar from "../layout/Sidebar";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  img: string;
  bio: string;
  expertise: string[];
  email: string;
  linkedin: string;
  projects: number;
  recentProjects: string[];
}

const teamMembers: TeamMember[] = [
   {
    id: 1,
    name: "Adan Siyad",
    role: "CEO & Founder",
    img: "/images/founder.png",
    bio: "Leads Northstart Systems’ vision, partnerships, and execution across ICT, security, and digital transformation. Brings deep experience in operations, procurement, logistics, and delivery across Africa, Asia, and the Middle East, focused on building reliable solutions with measurable impact.",
    expertise: [
      "Leadership & Strategy",
      "Procurement & Operations",
      "Client Partnerships",
      "Program & Delivery Management",
      "Risk & Compliance",
      "Business Development",
    ],
    email: "ceo@northstartsystems.com",
    linkedin: "https://www.linkedin.com/company/northstartsystems/",
    projects: 120,
    recentProjects: [
      "Company launch and growth roadmap (2014–present)",
      "Government and enterprise ICT delivery programs",
      "Security solutions division rollout (CCTV, access control, perimeter)",
      "Regional service expansion across East Africa",
      "Procurement systems advisory and implementation",
      "Strategic partnerships with suppliers & integrators",
    ],
  },
  {
    id: 2,
    name: "Margaret Gathoni",
    role: "Tech Lead Developer",
    img: "/images/developer.png",
    bio: "Software developer with 4+ years of experience in full-stack development, API design, cloud solutions and systems integration. Skilled in Devops, cloud services, and CI/CD with strong expertise in building scalable, secure, and data-driven solutions. Passionate about aligning technology with business impact and mentoring teams to deliver innovative, high-performance products.",
    expertise: [
      "Python (Django, FastAPI, Flask)",
      "React & TypeScript",
      "PostgreSQL / MySQL",
      "API Development & Integration",
      "Cloud & DevOps (AWS, Docker, Azure)",
      "System Architecture",
    ],
    email: "techlead@northstartsystems.com",
    linkedin: "https://www.linkedin.com/in/margaret-gathoni/",
    projects: 15,
    recentProjects: [
      "Northstart Systems Website - Company Portfolio",
      "CIHEB Kenya Vendor Portal – Automated vendor prequalification and procurement workflow",
      "CIHEB Data Integration Project – API linkages between KenyaEMR, DHIS2 & AIMS",
      "Mama Africa App – Flask & React maternal health companion platform",
      "Smart Mobi POS – Retail inventory and payment management system",
      "Mentorship Dashboard – PHP & Chart.js data visualization for performance tracking",
    ],
  }
];

const coreValues = [
  {
    icon: Shield,
    title: "Integrity",
    description: "We uphold the highest ethical standards in every project and partnership.",
    color: "from-blue-600 to-indigo-600",
    tint: "text-blue-700",
    soft: "bg-blue-50",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Continuously embracing modern technology to deliver superior solutions.",
    color: "from-purple-600 to-pink-600",
    tint: "text-purple-700",
    soft: "bg-purple-50",
  },
  {
    icon: Target,
    title: "Impact",
    description: "Creating meaningful change that empowers institutions and communities.",
    color: "from-emerald-600 to-teal-600",
    tint: "text-emerald-700",
    soft: "bg-emerald-50",
  },
  {
    icon: Heart,
    title: "Excellence",
    description: "Committed to delivering quality that exceeds expectations every time.",
    color: "from-orange-600 to-rose-600",
    tint: "text-orange-700",
    soft: "bg-orange-50",
  },
];

const milestones = [
  { year: "2014", event: "Company Founded", description: "Northstart Systems established in Nairobi" },
  { year: "2016", event: "First Major Contract", description: "Secured government ICT infrastructure project" },
  { year: "2018", event: "Regional Expansion", description: "Extended services across Kenya" },
  { year: "2020", event: "50+ Projects", description: "Reached a major delivery milestone" },
  { year: "2023", event: "Security Division", description: "Launched comprehensive security solutions unit" },
  { year: "2025", event: "Digital Innovation", description: "Leading modern web & security solutions in East Africa" },
  { year: "2026", event: "Future Vision", description: "Expanding impact across Africa and beyond" },
];

const stats = [
  { number: "11+", label: "Years in Business", icon: Calendar },
  { number: "70+", label: "Projects Completed", icon: Award },
  { number: "50+", label: "Happy Clients", icon: Users },
  { number: "5", label: "Core Team", icon: Zap },
];

const offices = [
  { city: "Nairobi", country: "Kenya", address: "Westlands, Nairobi", type: "Headquarters", icon: Building2 },
  { city: "East Africa", country: "Regional Coverage", address: "Kenya, Uganda, Tanzania", type: "Service Area", icon: Globe },
];

const AboutDetails = () => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const member = useMemo(() => selectedMember, [selectedMember]);

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <Sidebar />

      <main className="flex-1 p-6 md:p-10 space-y-12">
        <Helmet>
          <title>About Details | Northstart Systems</title>
        </Helmet>

        {/* Page Header */}
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur md:p-10">
          <div className="pointer-events-none absolute -top-24 left-1/4 h-64 w-64 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="pointer-events-none absolute top-10 right-1/4 h-72 w-72 rounded-full bg-indigo-200/35 blur-3xl" />
          <div className="relative">
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Built on integrity. Delivered with{" "}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                excellence.
              </span>
            </h1>

            <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-600 md:text-lg">
              A deeper look into our journey, values, team, and where we operate focused on clarity,
              reliability, and real-world impact.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="rounded-3xl border border-slate-900 bg-white/80 p-6 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <stat.icon className="mb-3 h-8 w-8 text-indigo-600" />
              <div className="mb-1 text-4xl font-extrabold text-slate-900">{stat.number}</div>
              <div className="text-xl font-semibold text-slate-900">{stat.label}</div>
            </div>
          ))}
        </section>

        {/* Founder Message (lighter, modern, still premium) */}
        <section id="founder">
          <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white/85 shadow-sm backdrop-blur">
            <header className="relative bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-8">
              <div className="pointer-events-none absolute inset-0 opacity-20" aria-hidden="true">
                <div className="absolute top-4 left-4 text-white/60">
                  <Code className="h-6 w-6" />
                </div>
                <div className="absolute bottom-4 right-4 text-white/60">
                  <Users className="h-5 w-5" />
                </div>
              </div>

              <div className="relative flex items-center gap-5">
                <div className="h-20 w-20 rounded-full bg-white/20 p-1 ring-1 ring-white/30">
                  <img
                    src="/images/founder.png"
                    alt="Founder of Northstart Systems"
                    className="h-full w-full rounded-full object-cover bg-white"
                  />
                </div>

                <div>
                  <h3 className="text-2xl font-extrabold text-white md:text-3xl">
                    Message from the Founder
                  </h3>
                  <p className="mt-1 flex items-center text-white/85">
                    <Award className="mr-2 h-4 w-4" />
                    CEO & Founder, Northstart Systems
                  </p>
                </div>
              </div>
            </header>

            <div className="space-y-6 p-8 text-slate-700 md:p-10">
              <blockquote className="relative">
                <div className="absolute -left-2 -top-4 select-none text-7xl font-serif text-indigo-200/60">
                  “
                </div>
                <p className="pl-10 text-lg italic font-bold leading-relaxed text-slate-800 md:text-xl">
                  As the founder of Northstart System Company Limited, I established this company in 2014
                  with a clear vision: to provide reliable, cutting-edge ICT and security solutions that
                  empower institutions, safeguard communities, and fuel digital transformation across Kenya
                  and beyond.
                </p>
              </blockquote>

              <p className="text-base leading-relaxed md:text-lg">
                With over 20 years of experience in procurement, logistics, operations, and supply chain
                management serving in both humanitarian and commercial sectors. I have led complex programs
                across Africa, Asia, and the Middle East.
              </p>

              <p className="text-base leading-relaxed md:text-lg">
                Northstart was born from this passion and experience. Our goal is to deliver professional
                services in web design and development, e-procurement solutions, ICT system sales, brand
                agency partnerships, and security installations including CCTV, screening systems, and
                perimeter fencing.
              </p>

              <p className="text-base leading-relaxed md:text-lg">
                As we continue to grow, our commitment remains anchored in integrity, innovation, and impact.
                Whether working with governments, private businesses, or development organizations, we tailor
                our services to support sustainable and secure digital operations.
              </p>

              <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 p-6">
                <p className="text-xl font-extrabold text-indigo-700 md:text-2xl">
                  Welcome to Northstart,  where technology meets purpose.
                </p>
                <p className="mt-2 font-semibold text-slate-900 italic">Founder & CEO</p>
              </div>
            </div>
          </article>
        </section>

        {/* Core Values */}
        <section id="values" className="rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur md:p-10">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Our Core Values
            </h2>
            <p className="mt-2 text-base text-indigo-900 italic font-bold md:text-lg">Principles that guide everything we do.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {coreValues.map((value, i) => (
              <div
                key={i}
                className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${value.color} shadow-sm`}>
                  <value.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900">{value.title}</h3>
                <p className="mt-2 text-xl font-bold leading-relaxed text-gray-600">{value.description}</p>

                <div className={`mt-4 rounded-4xl ${value.soft} px-4 py-3`}>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <CheckCircle2 className={`h-4 w-4 ${value.tint}`} />
                    How it shows up
                  </div>
                  <p className="mt-1 text-sm text-slate-900">
                    {value.title === "Integrity" && "We communicate clearly and deliver transparently."}
                    {value.title === "Innovation" && "We pick the best tools for the job no buzzwords."}
                    {value.title === "Impact" && "We focus on outcomes that support real operations."}
                    {value.title === "Excellence" && "We ship work we’re proud to stand behind."}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline – Modern Vertical Roadmap */}
        <section
          id="timeline"
          className="relative rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur md:p-14"
        >
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Our Journey
            </h2>
            <p className="mt-2 text-base text-slate-600 md:text-lg">
              A decade of growth and digital transformation.
            </p>
          </div>

          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-1/2 top-0 hidden h-full w-[2px] -translate-x-1/2 bg-gradient-to-b from-indigo-200 via-blue-300 to-indigo-200 md:block" />

            <div className="space-y-16">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`relative flex flex-col items-center md:flex-row ${
                    i % 2 === 0 ? "md:justify-start" : "md:justify-end"
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-1/2 hidden -translate-x-1/2 md:block">
                    <div className="h-5 w-5 rounded-full bg-indigo-600 ring-4 ring-indigo-100 shadow-md" />
                  </div>

                  {/* Card */}
                  <div
                    className={`w-full md:w-[45%] rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                      i % 2 === 0 ? "md:mr-auto md:pr-12" : "md:ml-auto md:pl-12"
                    }`}
                  >
                    <div className="inline-flex items-center rounded-full bg-indigo-50 px-4 py-2 text-sm font-extrabold text-indigo-700 ring-1 ring-indigo-100">
                      {m.year}
                    </div>

                    <h3 className="mt-4 text-xl font-extrabold text-slate-900">
                      {m.event}
                    </h3>

                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      {m.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section id="team" className="rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur md:p-10">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Our Team
            </h2>
            <p className="mt-2 text-base text-slate-600 md:text-lg">Meet the professionals behind Northstart.</p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {teamMembers.map((person) => (
              <div
                key={person.id}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="relative mb-6">
                  <div className="mx-auto h-40 w-40 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 p-1 shadow-sm">
                    <img
                      src={person.img}
                      alt={person.name}
                      className="h-full w-full rounded-full object-cover bg-white"
                    />
                  </div>

                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md">
                    {person.projects}+ Projects
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-2xl font-extrabold text-slate-900">{person.name}</h3>
                  <p className="mt-1 text-base font-bold text-indigo-600">{person.role}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{person.bio}</p>
                </div>

                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {person.expertise.map((skill, i) => (
                    <span
                      key={i}
                      className="rounded-full bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                <button
                  onClick={() => setSelectedMember(person)}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-indigo-600 px-6 py-3 text-base font-bold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700"
                >
                  View Portfolio
                  <ExternalLink className="ml-2 h-5 w-5" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <div className="inline-block rounded-2xl border border-indigo-200 bg-indigo-50 px-6 py-4">
              <p className="text-sm font-semibold text-slate-700">
                <span className="font-extrabold text-indigo-700">Growing team:</span> We’re expanding more
                talented professionals joining soon.
              </p>
            </div>
          </div>
        </section>

        {/* Locations */}
        <section id="locations" className="rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur md:p-10">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Our Locations
            </h2>
            <p className="mt-2 text-base text-slate-600 md:text-lg">Serving clients across East Africa.</p>
          </div>

          <div className="mx-auto grid max-w-3xl gap-4 md:grid-cols-2">
            {offices.map((office, i) => (
              <div
                key={i}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <office.icon className="mb-4 h-12 w-12 text-indigo-600" />
                <h3 className="text-2xl font-extrabold text-slate-900">{office.city}</h3>
                <p className="mt-1 text-base font-semibold text-slate-700">{office.country}</p>
                <p className="mt-2 text-sm text-slate-600">{office.address}</p>
                <span className="mt-4 inline-flex rounded-full bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
                  {office.type}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Modal */}
        {member && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={() => setSelectedMember(null)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-white">
                <button
                  onClick={() => setSelectedMember(null)}
                  className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="flex items-center gap-5">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="h-24 w-24 rounded-full border-4 border-white/40 object-cover"
                  />
                  <div>
                    <h3 className="text-3xl font-extrabold">{member.name}</h3>
                    <p className="mt-1 text-white/85">{member.role}</p>
                    <p className="mt-2 text-white/75">{member.projects}+ Projects Completed</p>
                  </div>
                </div>
              </div>

              <div className="space-y-7 p-8">
                <div>
                  <h4 className="text-xl font-extrabold text-slate-900">About</h4>
                  <p className="mt-3 text-slate-700 leading-relaxed">{member.bio}</p>
                </div>

                <div>
                  <h4 className="text-xl font-extrabold text-slate-900">Expertise</h4>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {member.expertise.map((skill, i) => (
                      <span
                        key={i}
                        className="rounded-xl bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 ring-1 ring-indigo-100"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-extrabold text-slate-900">Contact</h4>
                  <div className="mt-3 space-y-3">
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center text-slate-700 transition hover:text-indigo-700"
                    >
                      <Mail className="mr-3 h-5 w-5 text-indigo-600" />
                      {member.email}
                    </a>
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-slate-700 transition hover:text-indigo-700"
                    >
                      <Linkedin className="mr-3 h-5 w-5 text-indigo-600" />
                      LinkedIn Profile
                    </a>
                    <div className="flex items-center text-slate-700">
                      <Phone className="mr-3 h-5 w-5 text-indigo-600" />
                      +254 7000413385 / +254705009102
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 p-6">
                  <h4 className="text-lg font-extrabold text-slate-900">Recent Projects</h4>
                  <ul className="mt-3 space-y-2 text-slate-700">
                    {member.recentProjects.map((project, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2 text-xl text-indigo-600">•</span>
                        <span>{project}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setSelectedMember(null)}
                    className="rounded-2xl bg-slate-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AboutDetails;